import { AppIcon } from '@/components/ui/app-icon';
import { Logo } from '@/components/logo.component';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  iconArrowsRotate,
  iconCircleExclamation,
  iconHouse,
} from '@/lib/icons';

export const Route = createFileRoute('/auth/error')({
  component: AuthErrorPage,
});

function AuthErrorPage() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      setErrorCode(error);

      // Map common Clerk error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        access_denied: 'Access was denied. Please try signing in again.',
        invalid_request: 'Invalid request. Please try again.',
        user_cancelled:
          "Sign in was cancelled. Please try again when you're ready.",
        form_identifier_exists:
          'An account with this email already exists. Please sign in instead.',
        form_password_pwned:
          'This password has been compromised. Please choose a different password.',
        form_param_format_invalid:
          'Invalid format. Please check your information and try again.',
        identifier_already_signed_in:
          "You're already signed in. Redirecting to home...",
        strategy_for_user_invalid:
          'Invalid sign in method for this account. Please try a different method.',
        oauth_access_denied:
          'OAuth access was denied. Please try again or use a different sign in method.',
        oauth_callback_error:
          'There was an error during authentication. Please try again.',
      };

      setErrorMessage(
        errorDescription ||
          errorMessages[error] ||
          'An unexpected error occurred during authentication. Please try again.'
      );

      if (error === 'identifier_already_signed_in') {
        setTimeout(() => navigate({ to: '/' }), 2000);
      }
    } else {
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  }, [navigate]);

  return (
    <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-white to-slate-50 p-4 dark:bg-black'>
      <div className='w-full max-w-md'>
        <div className='mb-8 text-center'>
          <Logo className='mx-auto mb-4 h-8 w-auto' />
        </div>

        <Card className='border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900'>
          <CardContent className='p-8'>
            <div className='flex flex-col items-center text-center'>
              <div className='mb-4 rounded-full bg-red-100 p-3 dark:bg-red-900/20'>
                <AppIcon
                  icon={iconCircleExclamation}
                  className='text-red-600 dark:text-red-400'
                  style={{ fontSize: 32 }}
                />
              </div>

              <h2 className='mb-2 text-xl font-semibold text-foreground'>
                Authentication Error
              </h2>

              <p className='mb-6 text-sm text-muted-foreground'>
                {errorMessage}
              </p>

              {errorCode && (
                <p className='mb-6 font-mono text-xs text-muted-foreground'>
                  Error code: {errorCode}
                </p>
              )}

              <div className='flex w-full gap-3'>
                <Button
                  variant='outline'
                  className='flex-1'
                  onClick={() => navigate({ to: '/' })}
                >
                  <AppIcon
                    icon={iconHouse}
                    className='mr-2'
                    style={{ fontSize: 16 }}
                  />
                  Go Home
                </Button>

                <Button
                  className='flex-1'
                  onClick={() => navigate({ to: '/auth/login' })}
                >
                  <AppIcon
                    icon={iconArrowsRotate}
                    className='mr-2'
                    style={{ fontSize: 16 }}
                  />
                  Try Again
                </Button>
              </div>

              <div className='mt-6 w-full border-t border-border pt-6'>
                <p className='text-xs text-muted-foreground'>
                  If this problem persists, please contact{' '}
                  <a
                    href='mailto:support@cleanlist.ai'
                    className='text-primary hover:underline'
                  >
                    support@cleanlist.ai
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AuthErrorPage;
